import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { makeStyles } from "@material-ui/core/styles/index"
import { AppBar, Toolbar } from "@material-ui/core/index"
import { Link, navigate } from "gatsby"
import logo from "../images/Face-institute-logo.png"
import { injectIntl, changeLocale } from "gatsby-plugin-intl"
import Button from "@material-ui/core/Button"
import Menu from "@material-ui/core/Menu"
import MenuItem from "@material-ui/core/MenuItem"
import MenuIcon from "@material-ui/icons/Menu"
import CloseIcon from "@material-ui/icons/Close"
import ExpandMore from "@material-ui/icons/ExpandMore"

const BAR_BG = "#000"
const NAV_TEXT = "#cfcccf"
const NAV_TEXT_HOVER = "#fff"
const SELECTED_TEXT = "#f2efe9"
const SELECTED_BG = "rgba(239, 234, 226, 0.09)"
const SELECTED_BORDER = "#b9b2a6"
const HAIRLINE = "#2a2a2a"
const MOBILE = "@media (max-width:959px)"

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
    borderBottom: `1px solid ${HAIRLINE}`,
    background: BAR_BG,
    color: "#fff",
    margin: "auto",
    maxWidth: "1920px",
  },
  // The dropdowns stay solid black so hero text never shows through them.
  appBarOverHero: {
    position: "relative",
    borderBottom: "none",
    background: "linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0))",
    color: "#fff",
    margin: "auto",
    maxWidth: "1920px",
    // Solid on phones: the light logo would sit on the photo's light backdrop.
    [MOBILE]: {
      background: BAR_BG,
    },
  },
  toolbar: {
    flexWrap: "wrap",
    minHeight: "90px",
    [MOBILE]: {
      minHeight: "auto",
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
  toolbarTitle: {
    marginLeft: "3em!important",
    [MOBILE]: {
      flexGrow: 1,
      marginLeft: "0!important",
    },
  },
  logo: {
    marginLeft: "1.8em",
    width: "120px",
    display: "block",
    [MOBILE]: {
      marginLeft: 0,
      width: "112px",
    },
  },
  spacer: {
    flexGrow: 1,
    [MOBILE]: {
      display: "none",
    },
  },
  menuButton: {
    display: "none",
    [MOBILE]: {
      display: "inline-flex",
      alignItems: "center",
      background: "none",
      border: "none",
      color: "#fff",
      fontFamily: "inherit",
      fontSize: "13px",
      fontWeight: 500,
      letterSpacing: "0.1em",
      cursor: "pointer",
      padding: theme.spacing(0.75, 0.5),
      marginLeft: theme.spacing(0.5),
      "&:focus": { outline: "none" },
      "&:focus-visible": {
        outline: `2px solid ${SELECTED_BORDER}`,
        outlineOffset: 2,
      },
    },
  },
  menuButtonIcon: {
    fontSize: "26px",
    marginRight: "6px",
  },
  // Desktop: an inline bar. Mobile: a panel that drops from under the toolbar
  // when the menu button is pressed. Same markup either way, so every link is
  // in the server-rendered HTML exactly once and stays crawlable.
  nav: {
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(3),
    [MOBILE]: {
      display: "block",
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      marginLeft: 0,
      backgroundColor: BAR_BG,
      maxHeight: 0,
      overflow: "hidden",
      transition: "max-height 240ms ease",
      zIndex: 1300,
    },
  },
  navOpen: {
    [MOBILE]: {
      maxHeight: "calc(100vh - 70px)",
      "@supports (height: 100dvh)": {
        maxHeight: "calc(100dvh - 70px)",
      },
      overflowY: "auto",
      overscrollBehavior: "contain",
      WebkitOverflowScrolling: "touch",
      boxShadow: "0 18px 36px rgba(0,0,0,0.6)",
    },
  },
  navList: {
    display: "flex",
    alignItems: "center",
    listStyle: "none",
    margin: 0,
    padding: 0,
    [MOBILE]: {
      display: "block",
      padding: theme.spacing(0, 0, 4),
      borderTop: `1px solid ${HAIRLINE}`,
    },
  },
  navGroup: {
    position: "relative",
    listStyle: "none",
    margin: 0,
    [MOBILE]: {
      position: "static",
      display: "block",
      width: "100%",
      borderBottom: `1px solid ${HAIRLINE}`,
    },
  },
  navItem: {
    color: NAV_TEXT,
    fontSize: "15px",
    fontWeight: 500,
    letterSpacing: "0.06em",
    lineHeight: 1.2,
    textDecoration: "none",
    whiteSpace: "nowrap",
    padding: theme.spacing(1, 2),
    background: "none",
    border: "1px solid transparent",
    borderRadius: 999,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    appearance: "none",
    transition: "background .2s ease, color .2s ease, border-color .2s ease",
    "&:hover": {
      textDecoration: "none",
      color: NAV_TEXT_HOVER,
    },
    "&:focus": { outline: "none" },
    "&:focus-visible": {
      outline: `2px solid ${SELECTED_BORDER}`,
      outlineOffset: 2,
    },
  },
  navGroupHeading: {
    [MOBILE]: {
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      textAlign: "left",
      whiteSpace: "normal",
      fontSize: "18px",
      color: "#fff",
      border: "none",
      borderRadius: 0,
      padding: theme.spacing(2.25, 3),
    },
  },
  navItemLeaf: {
    [MOBILE]: {
      display: "block",
      width: "100%",
      textAlign: "left",
      whiteSpace: "normal",
      fontSize: "18px",
      color: "#fff",
      border: "none",
      borderRadius: 0,
      padding: theme.spacing(2.25, 3),
    },
  },
  navItemActive: {
    color: SELECTED_TEXT,
    backgroundColor: SELECTED_BG,
    borderColor: SELECTED_BORDER,
    fontWeight: 600,
    [MOBILE]: {
      borderRadius: 0,
      borderLeft: `3px solid ${SELECTED_BORDER}`,
    },
  },
  caret: {
    fontSize: "20px",
    marginLeft: "4px",
    transition: "transform 200ms ease",
    [MOBILE]: {
      fontSize: "28px",
      marginLeft: theme.spacing(2),
      color: NAV_TEXT,
    },
  },
  caretOpen: {
    [MOBILE]: {
      transform: "rotate(180deg)",
    },
  },
  submenu: {
    position: "absolute",
    top: "100%",
    left: 0,
    minWidth: "250px",
    backgroundColor: BAR_BG,
    border: `1px solid ${HAIRLINE}`,
    listStyle: "none",
    margin: 0,
    padding: theme.spacing(0.75, 0),
    zIndex: 1200,
    visibility: "hidden",
    opacity: 0,
    transition: "opacity 120ms ease",
    // In the phone panel the submenu stays in flow so it can't be clipped.
    [MOBILE]: {
      display: "none",
      position: "static",
      minWidth: 0,
      width: "100%",
      border: "none",
      margin: 0,
      padding: theme.spacing(0, 0, 1.5),
      visibility: "visible",
      opacity: 1,
      boxShadow: "none",
    },
  },
  submenuOpen: {
    visibility: "visible",
    opacity: 1,
    [MOBILE]: {
      display: "block",
    },
  },
  navGroupHoverable: {
    "@media (hover: hover) and (min-width:960px)": {
      "&:hover $submenu": {
        visibility: "visible",
        opacity: 1,
      },
      "&:focus-within $submenu": {
        visibility: "visible",
        opacity: 1,
      },
    },
  },
  submenuItem: {
    listStyle: "none",
    margin: 0,
  },
  submenuLink: {
    color: NAV_TEXT,
    fontSize: "15px",
    fontWeight: 500,
    letterSpacing: "0.06em",
    textDecoration: "none",
    display: "block",
    padding: theme.spacing(1.25, 2.5),
    whiteSpace: "nowrap",
    border: "1px solid transparent",
    borderRadius: 999,
    margin: theme.spacing(0.25, 1),
    transition: "background .2s ease, color .2s ease, border-color .2s ease",
    "&:hover": {
      textDecoration: "none",
      color: NAV_TEXT_HOVER,
    },
    [MOBILE]: {
      fontSize: "17px",
      color: NAV_TEXT,
      whiteSpace: "normal",
      padding: theme.spacing(1.75, 3, 1.75, 5),
      border: "none",
      borderLeft: "3px solid transparent",
      borderRadius: 0,
      margin: 0,
      "&:hover": { color: NAV_TEXT_HOVER },
      "&$navItemActive": {
        color: SELECTED_TEXT,
        borderLeftColor: SELECTED_BORDER,
      },
    },
  },
  localMenu: {
    backgroundColor: BAR_BG,
    borderRadius: 0,
  },
  localMenuItem: {
    color: "white",
  },
  localLink: {
    textDecoration: "none",
    color: "white",
    "&:hover": {
      textDecoration: "none",
    },
  },
  label: {
    color: "white",
  },
  localFlag: {
    width: "18px",
    height: "auto",
    marginLeft: "8px",
    borderRadius: "2px",
    verticalAlign: "middle",
    display: "inline-block",
    border: "1px solid rgba(255, 255, 255, 0.25)",
  },
}))

const getUrl = (locale, url) => {
  if (locale === "nl") {
    if (url === "") {
      return "/"
    }
    return "/" + url + "/"
  }
  return `/${locale}/${url}/`
}

// Deep Plane is the main facelift page, so it appears both under Facelift and
// as the facelift entry in Treatments; International Patients is likewise
// top-level and repeated under Facelift.
const buildNav = (t) => [
  {
    key: "facelift",
    label: t("navDrawer.facelift"),
    children: [
      { url: "deep-plane-facelift", label: t("navDrawer.deep") },
      { url: "facelift-revision", label: t("navDrawer.faceliftRevision") },
      { url: "male-facelift", label: t("navDrawer.maleFaceLift") },
      {
        url: "international-patients",
        label: t("navDrawer.internationalPatients"),
      },
    ],
  },
  {
    key: "international",
    label: t("navDrawer.internationalPatients"),
    url: "international-patients",
  },
  {
    key: "treatments",
    label: t("navDrawer.treatments"),
    children: [
      { url: "treatments", label: t("navDrawer.allTreatments") },
      { url: "deep-plane-facelift", label: t("navDrawer.deep") },
      { url: "rhinoplasty", label: t("navDrawer.rhinoplasty") },
      { url: "fillers-and-btx", label: t("navDrawer.fillers") },
    ],
  },
  {
    key: "about",
    label: t("navDrawer.aboutUs"),
    children: [
      {
        url: "about-face-institute",
        label: t("navDrawer.aboutFaceInstitute"),
      },
      { url: "about", label: t("navDrawer.doctors") },
      { url: "reviews", label: t("navDrawer.reviews") },
      { url: "contact", label: t("navDrawer.contact") },
      { url: "education", label: t("navDrawer.education") },
    ],
  },
]

// Dutch is served at the root (faceinstitute.nl/...), but gatsby-plugin-intl's
// changeLocale always builds "/<locale>/<path>" and would land visitors on the
// duplicate /nl/ pages. Switching to Dutch therefore strips the locale prefix
// off the current path and navigates to the bare URL, while keeping the
// plugin's stored language choice in sync. English still goes through
// changeLocale — /en/... is its real URL.
const changeToDutch = () => {
  if (typeof window === "undefined") return
  const { pathname, search } = window.location
  const barePath = pathname.replace(/^\/(en|nl)(?=\/|$)/, "") || "/"
  localStorage.setItem("gatsby-intl-language", "nl")
  navigate(`${barePath}${search}`)
}

const MainToolbar = ({ intl, black }) => {
  const classes = useStyles()
  const locale = intl.locale
  const t = (id) => intl.formatMessage({ id })
  const navItems = buildNav(t)

  const [openKey, setOpenKey] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)
  const [local] = useState(intl.locale === "nl" ? "nl" : `${intl.locale}`)
  const barRef = useRef(null)
  const menuButtonRef = useRef(null)

  useEffect(() => {
    if (!openKey && !mobileOpen) {
      return undefined
    }
    const onDocumentClick = (event) => {
      if (barRef.current && !barRef.current.contains(event.target)) {
        setOpenKey(null)
        setMobileOpen(false)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        // Closing the phone panel would leave focus on a hidden link.
        if (mobileOpen && menuButtonRef.current) {
          menuButtonRef.current.focus()
        }
        setOpenKey(null)
        setMobileOpen(false)
      }
    }
    document.addEventListener("mousedown", onDocumentClick)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onDocumentClick)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [openKey, mobileOpen])

  const closeAll = () => {
    setOpenKey(null)
    setMobileOpen(false)
  }

  const toggleMobile = () => {
    if (mobileOpen) {
      closeAll()
      return
    }
    const path = window.location.pathname.replace(/^\/(en|nl)(?=\/|$)/, "")
    const current = navItems.find(
      (item) =>
        item.children &&
        item.children.some((child) => path === `/${child.url}/`)
    )
    setOpenKey(current ? current.key : null)
    setMobileOpen(true)
  }

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      className={black ? classes.appBar : classes.appBarOverHero}
      ref={barRef}
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarTitle}>
          <Link
            variant="button"
            to={intl.locale === "nl" ? "/" : `/${intl.locale}/`}
          >
            <img src={logo} className={classes.logo} alt="Face Institute"></img>
          </Link>
        </div>

        <nav
          className={`${classes.nav} ${mobileOpen ? classes.navOpen : ""}`}
          id="main-nav"
        >
          <ul className={classes.navList}>
            {navItems.map((item) =>
              item.children ? (
                <li
                  key={item.key}
                  className={`${classes.navGroup} ${classes.navGroupHoverable}`}
                >
                  <button
                    type="button"
                    className={`${classes.navItem} ${classes.navGroupHeading}`}
                    aria-haspopup="true"
                    aria-expanded={openKey === item.key}
                    aria-controls={`nav-menu-${item.key}`}
                    onClick={() =>
                      setOpenKey(openKey === item.key ? null : item.key)
                    }
                  >
                    <span>{item.label}</span>
                    <ExpandMore
                      className={`${classes.caret} ${
                        openKey === item.key ? classes.caretOpen : ""
                      }`}
                    />
                  </button>
                  <ul
                    id={`nav-menu-${item.key}`}
                    className={`${classes.submenu} ${
                      openKey === item.key ? classes.submenuOpen : ""
                    }`}
                  >
                    {item.children.map((child) => (
                      <li key={child.url} className={classes.submenuItem}>
                        <Link
                          to={getUrl(locale, child.url)}
                          activeClassName={classes.navItemActive}
                          className={classes.submenuLink}
                          onClick={closeAll}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              ) : (
                <li key={item.key} className={classes.navGroup}>
                  <Link
                    to={getUrl(locale, item.url)}
                    activeClassName={classes.navItemActive}
                    className={`${classes.navItem} ${classes.navItemLeaf}`}
                    onClick={closeAll}
                  >
                    <span>{item.label}</span>
                  </Link>
                </li>
              )
            )}
          </ul>
        </nav>

        <div className={classes.spacer} />

        <div>
          <Button
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleClick}
            classes={{
              label: classes.label,
            }}
          >
            {local}
            <img
              src={`/flags/${local === "nl" ? "NL" : "GB"}.svg`}
              alt=""
              aria-hidden="true"
              width="18"
              height="12"
              className={classes.localFlag}
            />
          </Button>
          <Menu
            id="simple-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            classes={{
              paper: classes.localMenu,
              list: classes.localMenuItem,
            }}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {/* Switching language keeps the visitor on the same page.
                Dutch uses changeToDutch (bare root URL, no /nl/ prefix —
                see note above); flag icons per the master redesign */}
            <MenuItem
              classes={classes.localMenuItem}
              onClick={() => {
                changeToDutch()
                handleClose()
              }}
            >
              <span className={classes.localLink}>
                Dutch
                <img
                  src="/flags/NL.svg"
                  alt=""
                  aria-hidden="true"
                  width="18"
                  height="12"
                  className={classes.localFlag}
                />
              </span>
            </MenuItem>
            <MenuItem
              classes={classes.localMenuItem}
              onClick={() => {
                changeLocale("en")
                handleClose()
              }}
            >
              <span className={classes.localLink}>
                English
                <img
                  src="/flags/GB.svg"
                  alt=""
                  aria-hidden="true"
                  width="18"
                  height="12"
                  className={classes.localFlag}
                />
              </span>
            </MenuItem>
          </Menu>
        </div>

        <button
          type="button"
          className={classes.menuButton}
          aria-expanded={mobileOpen}
          aria-controls="main-nav"
          onClick={toggleMobile}
          ref={menuButtonRef}
        >
          {mobileOpen ? (
            <CloseIcon className={classes.menuButtonIcon} />
          ) : (
            <MenuIcon className={classes.menuButtonIcon} />
          )}
          {t("navDrawer.menu")}
        </button>
      </Toolbar>
    </AppBar>
  )
}

MainToolbar.propTypes = {
  black: PropTypes.bool,
}

export default injectIntl(MainToolbar)
